/**
 * Camada 1 · campo de TEXTO LONGO.
 *
 * ---------------------------------------------------------------------------
 * POR QUE ELE EXISTE, e o numero que o comprou
 * ---------------------------------------------------------------------------
 * Medido em 06/09/2026: SETE `<textarea>` crus na plataforma, cada um com a propria
 * aparencia escrita a mao. Quatro em telas de app e tres dentro de packages. Trocar o
 * estilo do texto longo custava sete edicoes, e a setima sempre ficaria para tras.
 *
 * Palavras dele em 06/09/2026: *"NO CASO DE MUDAR UM ESTILO DE BOTAO PARA 10 APPS QUE
 * TEREI, QUERO FAZER ISSO EM UM PROMTO."* Esta peca e o mesmo raciocinio aplicado ao campo
 * de texto: a aparencia mora em `cx-campo`, e mudar o texto longo de toda a plataforma
 * passa a ser uma edicao.
 *
 * ---------------------------------------------------------------------------
 * ELE REUSA `cx-campo`, e NAO tem estilo proprio
 * ---------------------------------------------------------------------------
 * Se ele desenhasse a propria borda, o proprio raio e o proprio foco, um dia o `Campo` e o
 * `CampoTexto` divergiriam na mesma tela, lado a lado, e ninguem saberia qual dos dois esta
 * certo. Com a classe compartilhada, os dois mudam juntos ou nenhum muda.
 *
 * O UNICO ACRESCIMO E A ALTURA MINIMA E O `resize-y`: um `textarea` sem altura nasce com
 * duas linhas, e quem escreve um comunicado de tres paragrafos digitaria dentro de uma
 * fresta. Redimensionar so na VERTICAL, porque na horizontal ele estouraria a coluna do
 * formulario e produziria a rolagem lateral que a regra do mobile proibe.
 *
 * ---------------------------------------------------------------------------
 * A API PUBLICA E A MESMA DO `Campo`, DE PROPOSITO
 * ---------------------------------------------------------------------------
 * `rotulo`, `obrigatorio`, `erro` e `ajuda`, com o mesmo significado. Trocar um pelo outro
 * numa tela nao exige reaprender nada, e um formulario que mistura os dois nao fica com
 * dois vocabularios.
 */

import { useId, type TextareaHTMLAttributes } from 'react';
import { t } from '@complexity/i18n';

export interface CampoTextoProps
  extends Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, 'id'> {
  readonly rotulo: string;
  /** Marca visual, atributo required e anuncio ao leitor de tela, os tres de uma vez. */
  readonly obrigatorio?: boolean;
  /** Mensagem de erro. Presente, o campo fica invalido e a mensagem e associada a ele. */
  readonly erro?: string;
  /** Texto de apoio abaixo do campo. Escondido quando ha erro, para nao competir com ele. */
  readonly ajuda?: string;
  /**
   * Quantas linhas o campo mostra em repouso.
   *
   * O PADRAO E 6, e nao o 2 do HTML: o padrao do navegador nasceu para formulario de 1995 e
   * hoje so serve para esconder o que a pessoa esta escrevendo. Seis linhas cabem um
   * paragrafo inteiro sem rolar.
   */
  readonly linhas?: number;
}

export function CampoTexto({
  rotulo,
  obrigatorio = false,
  erro,
  ajuda,
  linhas = 6,
  className = '',
  ...resto
}: CampoTextoProps) {
  // useId gera um id estavel entre servidor e cliente. Contador proprio produz id diferente
  // nos dois lados e quebra a associacao rotulo/campo na hidratacao.
  const id = useId();
  const idErro = `${id}-erro`;
  const idAjuda = `${id}-ajuda`;

  return (
    <div className="flex flex-col gap-cx-1">
      <label htmlFor={id} className="text-cx-sm font-medium text-texto">
        {rotulo}
        {obrigatorio && (
          <>
            {/* O asterisco e decorativo: quem le com leitor de tela recebe a palavra inteira
                logo em seguida, porque "asterisco" nao significa nada falado. */}
            <span aria-hidden="true" className="ml-cx-1 text-erro">
              *
            </span>
            {/* PASSA PELO DICIONARIO, e ate 08/09/2026 nao passava. A classe `cx-so-leitor`
                esconde esta frase da tela: quem a recebe e quem usa leitor de tela, e
                por isso o defeito era invisivel. Com a tela inteira em ingles, a pessoa
                ouvia "Email, star, (obrigatório)". */}
            <span className="cx-so-leitor">{t('(obrigatório)')}</span>
          </>
        )}
      </label>

      <textarea
        id={id}
        rows={linhas}
        // `resize-y` e nao `resize`: na horizontal ele estouraria a coluna e produziria a
        // rolagem lateral que a regra do mobile proibe em qualquer largura.
        className={`cx-campo resize-y ${className}`}
        required={obrigatorio}
        aria-invalid={erro ? true : undefined}
        // Aponta para a mensagem que existe: com erro, a de erro; sem erro, a de ajuda.
        aria-describedby={erro ? idErro : ajuda ? idAjuda : undefined}
        {...resto}
      />

      {erro && (
        // role="alert" faz o leitor de tela anunciar assim que a mensagem aparece, em vez de
        // so ao chegar no campo, que seria tarde para quem ja tentou enviar.
        <p id={idErro} role="alert" className="text-cx-xs text-erro">
          {erro}
        </p>
      )}

      {!erro && ajuda && (
        <p id={idAjuda} className="text-cx-xs text-texto-suave">
          {ajuda}
        </p>
      )}
    </div>
  );
}
